import React, { useMemo } from 'react';
import { Card, CardContent } from '../../ui/card';
import SortableTable from '../../SortableTable';
import { getPictureSrc, getPictureCaption, projectLabel, formatDateFr } from '../ipHelpers';

const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-3">
    <h2 className="text-base font-semibold text-gray-800 dark:text-white">{children}</h2>
    {action}
  </div>
);

export default function IPOverviewTab({
  ip,
  assetsCall,
  visitCalls,
  picturesByCallId,
  onOpenPhotos,
  onGoToPhotos,
}) {
  const furnitures = useMemo(() => {
    if (!assetsCall?.furnitures) return [];
    return [...new Set(String(assetsCall.furnitures).split(',').map((f) => f.trim()).filter(Boolean))];
  }, [assetsCall]);

  // Projects with their visit count + last visit
  const projectStats = useMemo(() => {
    const map = new Map();
    visitCalls.forEach((c) => {
      const key = c.projectName || '—';
      if (!map.has(key)) map.set(key, { project: projectLabel(key), visits: 0, lastVisit: null });
      const e = map.get(key);
      e.visits += 1;
      if (c.date && (!e.lastVisit || new Date(c.date) > new Date(e.lastVisit))) e.lastVisit = c.date;
    });
    return [...map.values()];
  }, [visitCalls]);

  const projectColumns = useMemo(
    () => [
      { key: 'project', label: 'Projet' },
      { key: 'visits', label: 'Visites' },
      {
        key: 'lastVisit',
        label: 'Dernière visite',
        rawValue: (r) => r.lastVisit || '',
        render: (r) => (r.lastVisit ? formatDateFr(r.lastVisit, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'),
      },
    ],
    []
  );

  // 4 latest photos across all visits
  const recentPhotos = useMemo(() => {
    const sorted = [...visitCalls].filter((c) => c.date).sort((a, b) => new Date(b.date) - new Date(a.date));
    const out = [];
    for (const call of sorted) {
      const pics = picturesByCallId[call.id] || [];
      for (const pic of pics) {
        out.push({ pic, call });
        if (out.length >= 4) break;
      }
      if (out.length >= 4) break;
    }
    return out;
  }, [visitCalls, picturesByCallId]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Mobiliers présents */}
      <Card>
        <CardContent className="pt-6">
          <SectionTitle>Mobiliers présents</SectionTitle>
          {furnitures.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {furnitures.map((f, idx) => (
                <span
                  key={`${f}-${idx}`}
                  className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                >
                  {f}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">Aucun mobilier référencé.</p>
          )}
        </CardContent>
      </Card>

      {/* Projets associés */}
      <Card>
        <CardContent className="pt-6">
          <SectionTitle>Projets associés</SectionTitle>
          {projectStats.length > 0 ? (
            <SortableTable
              data={projectStats}
              columns={projectColumns}
              initialSortBy={{ key: 'visits', direction: 'descending' }}
              disableFilters
              hideCountLabel
              className="flex flex-col max-h-72 relative"
            />
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">Aucun projet associé.</p>
          )}
        </CardContent>
      </Card>

      {/* Dernières photos */}
      {recentPhotos.length > 0 && (
        <Card className="lg:col-span-2">
          <CardContent className="pt-6">
            <SectionTitle
              action={
                <button
                  type="button"
                  onClick={onGoToPhotos}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 inline-flex items-center gap-1"
                >
                  Tout voir →
                </button>
              }
            >
              Dernières photos
            </SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {recentPhotos.map(({ pic, call }, idx) => {
                const src = getPictureSrc(pic);
                if (!src) return null;
                const caption = getPictureCaption(pic);
                return (
                  <button
                    key={`recent-${idx}`}
                    type="button"
                    onClick={() => onOpenPhotos(call.id, picturesByCallId[call.id] || [], `Visite #${call.id}`)}
                    className="focus:outline-none"
                  >
                    <div className="relative w-full h-28 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      <img src={src} alt={caption || 'photo'} loading="lazy" className="w-full h-full object-cover" />
                      {caption && (
                        <div className="absolute inset-x-0 bottom-0 bg-black/60 text-[10px] text-white px-1.5 py-0.5 leading-tight line-clamp-2">
                          {caption}
                        </div>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                      {formatDateFr(call.date, { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
